import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Chip, FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress, Tooltip, Pagination, Stack
} from '@mui/material';
import {
  Add, Edit, PersonOff, Shield, AdminPanelSettings, Visibility
} from '@mui/icons-material';
import { userAPI, reportAPI } from '../services/api';
import toast from 'react-hot-toast';

const BLUE = '#00A8E8';
const DARK_BLUE = '#005B8F';

const ROLE_CONFIG = {
  superadmin: { label: 'Super Admin', color: '#7c3aed', bg: '#f3e8ff', icon: <Shield fontSize="small" /> },
  pii_admin:  { label: 'PII Admin',   color: '#b45309', bg: '#fef3c7', icon: <Shield fontSize="small" /> },
  admin:      { label: 'Admin',       color: DARK_BLUE, bg: '#dbeafe', icon: <AdminPanelSettings fontSize="small" /> },
  viewer:     { label: 'Viewer',      color: '#64748b', bg: '#f1f5f9', icon: <Visibility fontSize="small" /> }
};

function UserDialog({ open, onClose, onSave, editUser }) {
  const [form, setForm] = useState({ name: '', email: '', mobile: '', role: 'admin', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editUser) {
      setForm({ name: editUser.name, email: editUser.email, mobile: editUser.mobile || '', role: editUser.role, password: '' });
    } else {
      setForm({ name: '', email: '', mobile: '', role: 'admin', password: '' });
    }
    setError('');
  }, [editUser, open]);

  const handleSave = async () => {
    if (!form.name || !form.email || (!editUser && !form.password)) {
      setError('Name, email, and password (for new users) are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = { ...form };
      if (editUser && !form.password) delete data.password;
      if (editUser) {
        await userAPI.update(editUser._id, data);
        toast.success('User updated');
      } else {
        await userAPI.create(data);
        toast.success('User created');
      }
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, color: DARK_BLUE }}>
        {editUser ? 'Edit User' : 'Create Admin User'}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required fullWidth />
          <TextField label="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required fullWidth />
          <TextField label="Mobile" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={form.role} label="Role" onChange={e => setForm({ ...form, role: e.target.value })}>
              <MenuItem value="viewer">Viewer — Read only</MenuItem>
              <MenuItem value="admin">Admin — Full location access</MenuItem>
              <MenuItem value="pii_admin">PII Admin — Can view decrypted patient data & export</MenuItem>
              <MenuItem value="superadmin">Super Admin — All access</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={editUser ? 'New Password (leave blank to keep)' : 'Password'}
            type="password" value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            required={!editUser} fullWidth
            helperText="Min 8 characters"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSave} disabled={loading} variant="contained"
          sx={{ background: `linear-gradient(135deg, ${BLUE}, #86E000)`, borderRadius: 2, fontWeight: 700 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : editUser ? 'Save Changes' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const USERS_PER_PAGE = 10;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [userPage, setUserPage] = useState(1);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'superadmin';

  const [smsTestMobile, setSmsTestMobile] = useState('');
  const [smsTestName, setSmsTestName] = useState('');
  const [smsTestLoading, setSmsTestLoading] = useState(false);
  const [smsTestResult, setSmsTestResult] = useState(null);

  const handleTestSms = async () => {
    if (!/^\d{10}$/.test(smsTestMobile)) {
      setSmsTestResult({ success: false, message: 'Enter a valid 10-digit mobile number' });
      return;
    }
    setSmsTestLoading(true);
    setSmsTestResult(null);
    try {
      const res = await reportAPI.testSms(smsTestMobile, smsTestName || 'Test Patient');
      setSmsTestResult({ success: true, message: `SMS sent. MSG91 response: ${JSON.stringify(res.data.result?.data || res.data.result)}` });
    } catch (err) {
      setSmsTestResult({ success: false, message: err.response?.data?.message || 'SMS send failed' });
    } finally {
      setSmsTestLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await userAPI.getAll();
      setUsers(data.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await userAPI.delete(id);
      toast.success('User deactivated');
      fetchUsers();
    } catch {
      toast.error('Failed to deactivate');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: DARK_BLUE }}>User Management</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Manage admin portal access and roles</Typography>
        </Box>
        {isSuperAdmin && (
          <Button
            startIcon={<Add />} variant="contained"
            onClick={() => { setEditUser(null); setDialogOpen(true); }}
            sx={{ background: `linear-gradient(135deg, ${BLUE}, #86E000)`, borderRadius: 2, fontWeight: 700 }}
          >
            Add User
          </Button>
        )}
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: BLUE }} />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, color: DARK_BLUE, bgcolor: '#f0f9ff' } }}>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    {isSuperAdmin && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.slice((userPage - 1) * USERS_PER_PAGE, userPage * USERS_PER_PAGE).map(user => {
                    const role = ROLE_CONFIG[user.role] || ROLE_CONFIG.viewer;
                    return (
                      <TableRow key={user._id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.mobile || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            icon={role.icon}
                            label={role.label}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: role.bg, color: role.color, border: 'none' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: user.isActive ? '#dcfce7' : '#fee2e2',
                              color: user.isActive ? '#16a34a' : '#dc2626'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                          {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('en-IN') : 'Never'}
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell align="center">
                            <Tooltip title="Edit user">
                              <IconButton size="small" onClick={() => { setEditUser(user); setDialogOpen(true); }}>
                                <Edit fontSize="small" sx={{ color: BLUE }} />
                              </IconButton>
                            </Tooltip>
                            {user._id !== currentUser._id && (
                              <Tooltip title="Deactivate user">
                                <IconButton size="small" onClick={() => handleDeactivate(user._id)}>
                                  <PersonOff fontSize="small" sx={{ color: '#ef4444' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {!users.length && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ color: '#94a3b8', py: 6 }}>
                        No users found. Create the first admin user.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {Math.ceil(users.length / USERS_PER_PAGE) > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((userPage - 1) * USERS_PER_PAGE) + 1}–{Math.min(userPage * USERS_PER_PAGE, users.length)} of {users.length} users
              </Typography>
              <Stack>
                <Pagination
                  count={Math.ceil(users.length / USERS_PER_PAGE)}
                  page={userPage}
                  onChange={(_, val) => setUserPage(val)}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <UserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={fetchUsers}
        editUser={editUser}
      />

      {isSuperAdmin && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>SMS Delivery Test</Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              Send a test report SMS to any number to verify MSG91 delivery is working.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField
                label="Mobile Number (10 digits)"
                value={smsTestMobile}
                onChange={e => setSmsTestMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                size="small"
                sx={{ width: 220 }}
              />
              <TextField
                label="Patient Name (optional)"
                value={smsTestName}
                onChange={e => setSmsTestName(e.target.value)}
                size="small"
                sx={{ width: 200 }}
              />
              <Button
                variant="contained"
                onClick={handleTestSms}
                disabled={smsTestLoading}
                sx={{ background: '#667eea', '&:hover': { background: '#5a67d8' }, height: 40 }}
              >
                {smsTestLoading ? <CircularProgress size={18} color="inherit" /> : 'Send Test SMS'}
              </Button>
            </Box>
            {smsTestResult && (
              <Alert severity={smsTestResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                {smsTestResult.message}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
